import { avisosDaCena, type AvisoTabletop, type TipoAviso } from './avisos'
import type { IdReferenciaNormativa } from './normasAltura'
import { pontosDeAncoragem, type ReferenciaPonto } from './ancoragem'
import { analisarTalabartes } from './quedaEmAltura'
import { REFERENCIAS_ALTURA } from './referenciasAltura'
import type { CenaTabletop, MarcacaoChecklistAltura } from './tipos'

/**
 * O checklist do trabalho em altura (fase 4 do módulo): análise de risco,
 * permissão de trabalho, inspeção do EPI e plano de resgate, cada item com o
 * porquê e as referências normativas.
 *
 * Há dois tipos de item:
 *  - os **da mesa**, que o app confere na montagem (o talabarte alcança, a
 *    zona livre cabe, o balanço não bate...). Ninguém os marca à mão: eles
 *    mudam quando a montagem muda;
 *  - os **de campo**, que só quem está lá sabe (a PT foi emitida, o cinturão
 *    foi inspecionado...). O instrutor ou o aluno marca "feito" ou "não se
 *    aplica", e a marcação fica salva com a cena.
 *
 * Os itens e os porquês são PROVISÓRIOS: esperam a validação do instrutor,
 * como as referências em `normasAltura.ts`. TypeScript puro, com testes.
 */

export const SECOES_CHECKLIST_ALTURA = [
  { id: 'analise-de-risco', titulo: 'Análise de risco' },
  { id: 'permissao-de-trabalho', titulo: 'Permissão de trabalho e equipe' },
  { id: 'inspecao-epi', titulo: 'Inspeção do EPI' },
  { id: 'plano-de-resgate', titulo: 'Plano de emergência e resgate' },
] as const
export type IdSecaoChecklistAltura = (typeof SECOES_CHECKLIST_ALTURA)[number]['id']

/** O que o app confere na montagem. */
export type VerificacaoDaMesa =
  | { readonly tipo: 'aviso'; readonly aviso: TipoAviso }
  | { readonly tipo: 'altura-de-trabalho' }
  | { readonly tipo: 'todos-presos' }
  | { readonly tipo: 'resgate-montado' }

export interface ItemChecklistAltura {
  readonly id: string
  readonly secao: IdSecaoChecklistAltura
  readonly texto: string
  /** Por que o item existe: o que dá errado sem ele. */
  readonly porque: string
  readonly referencias: readonly IdReferenciaNormativa[]
  /** Presente: o app confere o item na montagem e ele não é marcado à mão. */
  readonly daMesa?: VerificacaoDaMesa
}

export const ITENS_CHECKLIST_ALTURA: readonly ItemChecklistAltura[] = [
  // --- análise de risco
  {
    id: 'ar-altura',
    secao: 'analise-de-risco',
    texto: 'É trabalho em altura: o trabalhador fica acima de 2 m do nível inferior, com risco de queda.',
    porque: 'É o que define se a NR-35 se aplica e se todo o resto deste checklist é obrigatório.',
    referencias: ['nr35-aplicacao'],
    daMesa: { tipo: 'altura-de-trabalho' },
  },
  {
    id: 'ar-todos-presos',
    secao: 'analise-de-risco',
    texto: 'Todo trabalhador na mesa está preso a um ponto de ancoragem, por talabarte ou pela corda.',
    porque: 'Quem está em altura sem sistema de proteção contra quedas não tem nada que pare a queda.',
    referencias: ['nr35-protecao-contra-quedas'],
    daMesa: { tipo: 'todos-presos' },
  },
  {
    id: 'ar-alcance',
    secao: 'analise-de-risco',
    texto: 'O talabarte alcança a ancoragem de onde o trabalhador está.',
    porque: 'Um talabarte curto obriga o trabalhador a se soltar para chegar ao ponto de trabalho.',
    referencias: ['nr35-protecao-contra-quedas'],
    daMesa: { tipo: 'aviso', aviso: 'talabarte-curto' },
  },
  {
    id: 'ar-zona-livre',
    secao: 'analise-de-risco',
    texto: 'A zona livre de queda cabe na altura livre abaixo da ancoragem.',
    porque: 'Se não cabe, o trabalhador bate no chão, no patamar ou na estrutura antes de o talabarte e o absorvedor pararem a queda.',
    referencias: ['nr35-protecao-contra-quedas'],
    daMesa: { tipo: 'aviso', aviso: 'queda-bate-no-chao' },
  },
  {
    id: 'ar-fator-de-queda',
    secao: 'analise-de-risco',
    texto: 'A ancoragem fica no nível do engate ou acima dele (fator de queda até 1).',
    porque: 'Quanto mais baixa a ancoragem, maior a queda livre e maior a força no corpo e no sistema quando a queda para.',
    referencias: ['nr35-protecao-contra-quedas', 'nr35-ancoragem'],
    daMesa: { tipo: 'aviso', aviso: 'fator-de-queda-alto' },
  },
  {
    id: 'ar-absorvedor',
    secao: 'analise-de-risco',
    texto: 'Há absorvedor de energia onde a queda livre passa do limite.',
    porque: 'Sem absorvedor, o talabarte para a queda de uma vez e a força vai inteira para o corpo e para a ancoragem.',
    referencias: ['nr35-protecao-contra-quedas'],
    daMesa: { tipo: 'aviso', aviso: 'talabarte-sem-absorvedor' },
  },
  {
    id: 'ar-pendulo',
    secao: 'analise-de-risco',
    texto: 'O balanço depois da queda não bate na estrutura.',
    porque: 'Com a ancoragem deslocada para o lado, a queda vira pêndulo e o trabalhador bate na parede, na borda ou no piso de baixo.',
    referencias: ['nr35-protecao-contra-quedas', 'nr35-ancoragem'],
    daMesa: { tipo: 'aviso', aviso: 'balanco-bate-na-estrutura' },
  },
  {
    id: 'ar-ancoragem-resistente',
    secao: 'analise-de-risco',
    texto: 'O ponto de ancoragem foi avaliado e resiste à carga de uma queda.',
    porque: 'O app não sabe a resistência de nada: um ponto bonito na mesa pode ser uma peça que se solta na queda.',
    referencias: ['nr35-ancoragem'],
  },
  {
    id: 'ar-entorno',
    secao: 'analise-de-risco',
    texto: 'Os riscos do local e do entorno foram levantados: rede elétrica, superfícies frágeis, clima, vento, pouca luz.',
    porque: 'A queda não é o único risco em altura: o choque elétrico, o telhado que cede e o vento derrubam quem está bem preso.',
    referencias: ['nr35-analise-de-risco'],
  },
  {
    id: 'ar-isolamento',
    secao: 'analise-de-risco',
    texto: 'A área abaixo está isolada e sinalizada.',
    porque: 'Uma ferramenta que cai de cima atinge quem passa embaixo; o isolamento também deixa livre a área do resgate.',
    referencias: ['nr35-analise-de-risco'],
  },
  // --- permissão de trabalho e equipe
  {
    id: 'pt-emitida',
    secao: 'permissao-de-trabalho',
    texto: 'Se a atividade não é rotineira, a permissão de trabalho foi emitida e aprovada pelo responsável antes de começar.',
    porque: 'A PT obriga a parar e conferir as medidas da análise de risco antes da subida, e deixa registrado quem autorizou.',
    referencias: ['nr35-permissao-de-trabalho'],
  },
  {
    id: 'pt-no-local',
    secao: 'permissao-de-trabalho',
    texto: 'A PT está disponível no local e com as medidas de proteção da análise de risco.',
    porque: 'Quem chega para trabalhar ou para resgatar precisa ver ali o que foi combinado.',
    referencias: ['nr35-permissao-de-trabalho'],
  },
  {
    id: 'pt-capacitacao',
    secao: 'permissao-de-trabalho',
    texto: 'Os trabalhadores são capacitados e autorizados para trabalho em altura.',
    porque: 'Saber usar o sistema (onde prender, como inspecionar, o que fazer na queda) faz parte da proteção.',
    referencias: ['nr35-capacitacao'],
  },
  {
    id: 'pt-aptidao',
    secao: 'permissao-de-trabalho',
    texto: 'Os trabalhadores estão aptos para trabalho em altura (avaliação de saúde em dia) e bem no dia.',
    porque: 'Tontura, mal-estar ou uma condição de saúde não avaliada viram queda.',
    referencias: ['nr35-capacitacao'],
  },
  // --- inspeção do EPI
  {
    id: 'epi-cinturao',
    secao: 'inspecao-epi',
    texto: 'Cinturão tipo paraquedista inspecionado: fitas, costuras, fivelas e argolas sem dano, e ajustado ao corpo.',
    porque: 'Uma costura cortada ou uma fivela frouxa abre na hora da queda.',
    referencias: ['nr35-protecao-contra-quedas', 'nr6-epi'],
  },
  {
    id: 'epi-talabarte',
    secao: 'inspecao-epi',
    texto: 'Talabarte e absorvedor inspecionados: sem cortes ou desgaste, e o absorvedor fechado (sem sinal de queda anterior).',
    porque: 'Um absorvedor que já trabalhou numa queda não tem mais como absorver a próxima.',
    referencias: ['nr35-protecao-contra-quedas', 'nr6-epi'],
  },
  {
    id: 'epi-conectores',
    secao: 'inspecao-epi',
    texto: 'Mosquetões e conectores fecham e travam sozinhos, sem trinca nem deformação.',
    porque: 'Um conector que não trava pode se soltar da ancoragem com o movimento do trabalho.',
    referencias: ['nr35-protecao-contra-quedas', 'nr6-epi'],
  },
  {
    id: 'epi-ca',
    secao: 'inspecao-epi',
    texto: 'Os EPIs têm certificado de aprovação (CA) válido e estão dentro da vida útil do fabricante.',
    porque: 'O CA e a vida útil garantem que o equipamento foi ensaiado e ainda vale o que promete.',
    referencias: ['nr6-epi'],
  },
  {
    id: 'epi-registro',
    secao: 'inspecao-epi',
    texto: 'A inspeção antes do uso foi feita e o equipamento reprovado foi retirado de uso.',
    porque: 'Equipamento reprovado que fica no meio dos outros acaba voltando para o trabalho.',
    referencias: ['nr35-protecao-contra-quedas', 'nr6-epi'],
  },
  // --- plano de emergência e resgate
  {
    id: 'resgate-montado',
    secao: 'plano-de-resgate',
    texto: 'O sistema de resgate está montado na mesa (corda de tração ou de vítima).',
    porque: 'Depois de parar a queda, o trabalhador fica pendurado: o resgate precisa estar pronto antes da subida, não ser improvisado depois.',
    referencias: ['nr35-emergencia-e-salvamento'],
    daMesa: { tipo: 'resgate-montado' },
  },
  {
    id: 'resgate-equipe',
    secao: 'plano-de-resgate',
    texto: 'Há equipe de resgate definida, treinada e disponível enquanto durar o trabalho.',
    porque: 'Sem gente pronta, o resgate espera alguém chegar.',
    referencias: ['nr35-emergencia-e-salvamento'],
  },
  {
    id: 'resgate-tempo',
    secao: 'plano-de-resgate',
    texto: 'O tempo até o resgate considera a suspensão inerte.',
    porque: 'Pendurado e parado no cinturão, o trabalhador pode passar mal em pouco tempo: o resgate tem pressa.',
    referencias: ['nr35-emergencia-e-salvamento'],
  },
  {
    id: 'resgate-equipamento',
    secao: 'plano-de-resgate',
    texto: 'Os equipamentos do resgate estão no local e inspecionados.',
    porque: 'Ir buscar a corda ou a polia na hora custa o tempo que o trabalhador pendurado não tem.',
    referencias: ['nr35-emergencia-e-salvamento'],
  },
  {
    id: 'resgate-comunicacao',
    secao: 'plano-de-resgate',
    texto: 'A comunicação com a equipe e o acionamento do socorro estão combinados.',
    porque: 'Todo mundo precisa saber quem avisa, por qual meio e para quem.',
    referencias: ['nr35-emergencia-e-salvamento'],
  },
]

/** O que fica salvo na cena: só as marcações dos itens de campo. */
export type ChecklistAlturaDaCena = Readonly<Record<string, MarcacaoChecklistAltura>>

export type SituacaoItemChecklist =
  /** item da mesa: a montagem atende */
  | 'atende'
  /** item da mesa: a montagem não atende */
  | 'nao-atende'
  /** item da mesa que só informa (a altura de trabalho), ou sem trabalhador para conferir */
  | 'informativo'
  | 'feito'
  | 'nao-se-aplica'
  | 'pendente'

export interface ItemAvaliado {
  readonly item: ItemChecklistAltura
  readonly situacao: SituacaoItemChecklist
  /** O porquê da situação de um item da mesa, com os nomes da cena. */
  readonly detalhe?: string
  /** O que tocar para ver o problema na mesa. */
  readonly idsObjetos: readonly string[]
}

export interface ChecklistAvaliado {
  readonly secoes: readonly { readonly id: IdSecaoChecklistAltura; readonly titulo: string; readonly itens: readonly ItemAvaliado[] }[]
  /** Itens pendentes de campo e itens da mesa que não atendem. */
  readonly emAberto: number
  readonly total: number
  /** Há trabalhador em altura na mesa: sem ele, o checklist não tem o que conferir. */
  readonly temTrabalhador: boolean
}

const usoDoPonto = (cena: CenaTabletop, ref: ReferenciaPonto) => {
  const objeto = cena.objetos.find((o) => o.id === ref.idObjeto)
  return objeto ? pontosDeAncoragem(objeto).find((p) => p.id === ref.idPonto)?.uso : undefined
}

const metros = (valor: number) => `${valor.toFixed(2).replace('.', ',')} m`

const listar = (nomes: readonly string[]) =>
  nomes.length <= 1 ? (nomes[0] ?? '') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`

function avaliarDaMesa(
  cena: CenaTabletop,
  verificacao: VerificacaoDaMesa,
  avisos: readonly AvisoTabletop[],
): Pick<ItemAvaliado, 'situacao' | 'detalhe' | 'idsObjetos'> {
  const trabalhadores = cena.objetos.filter((o) => o.tipo === 'trabalhador' && !o.oculto)
  if (trabalhadores.length === 0) {
    return { situacao: 'informativo', detalhe: 'Não há trabalhador na mesa para conferir.', idsObjetos: [] }
  }
  const nome = (id: string) => cena.objetos.find((o) => o.id === id)?.nome ?? id
  const talabartes = analisarTalabartes(cena)

  switch (verificacao.tipo) {
    case 'aviso': {
      const doTipo = avisos.filter((a) => a.tipo === verificacao.aviso)
      if (talabartes.length === 0) {
        return { situacao: 'informativo', detalhe: 'Nenhum trabalhador está preso por talabarte: não há o que conferir.', idsObjetos: [] }
      }
      if (doTipo.length === 0) return { situacao: 'atende', idsObjetos: [] }
      return { situacao: 'nao-atende', detalhe: doTipo.map((a) => a.mensagem).join(' '), idsObjetos: doTipo.flatMap((a) => (a.idObjeto ? [a.idObjeto] : [])) }
    }
    case 'altura-de-trabalho': {
      if (talabartes.length === 0) {
        return { situacao: 'informativo', detalhe: 'Prenda o trabalhador por um talabarte para o app medir a altura sobre o nível inferior.', idsObjetos: [] }
      }
      const limite = REFERENCIAS_ALTURA.alturaMinimaNr35M.valor
      const alturas = talabartes.map((t) => {
        const trabalhador = cena.objetos.find((o) => o.id === t.idTrabalhador)!
        return { id: t.idTrabalhador, altura: trabalhador.transformacao.posicao[1] - t.superficieAbaixoY }
      })
      const partes = alturas.map((a) => `${nome(a.id)} a ${metros(a.altura)}`)
      const acima = alturas.some((a) => a.altura > limite)
      return {
        situacao: 'informativo',
        detalhe: acima
          ? `${listar(partes)} do nível inferior: acima de ${metros(limite)}, é trabalho em altura.`
          : `${listar(partes)} do nível inferior: até ${metros(limite)}, a NR-35 não se aplica, mas o risco de queda continua.`,
        idsObjetos: alturas.map((a) => a.id),
      }
    }
    case 'todos-presos': {
      // preso por talabarte ou por uma corda (acesso por corda) que sai do engate e chega a uma ancoragem
      const presos = new Set(talabartes.map((t) => t.idTrabalhador))
      for (const c of cena.cordas ?? []) {
        const engate = c.percurso.find((r) => r.idPonto === 'engate-dorsal' || r.idPonto === 'engate-ventral')
        if (engate && c.percurso.some((r) => usoDoPonto(cena, r) === 'ancoragem')) presos.add(engate.idObjeto)
      }
      const soltos = trabalhadores.filter((t) => !presos.has(t.id))
      if (soltos.length === 0) return { situacao: 'atende', idsObjetos: [] }
      return {
        situacao: 'nao-atende',
        detalhe: `${listar(soltos.map((t) => t.nome))} ${soltos.length === 1 ? 'está' : 'estão'} sem talabarte nem corda ligada a uma ancoragem.`,
        idsObjetos: soltos.map((t) => t.id),
      }
    }
    case 'resgate-montado': {
      const resgate = (cena.cordas ?? []).filter((c) => c.funcao === 'tracao' || c.funcao === 'vitima')
      if (resgate.length > 0) return { situacao: 'atende', idsObjetos: [] }
      return {
        situacao: 'nao-atende',
        detalhe: 'Nenhuma corda de tração ou de vítima na mesa: monte o sistema de resgate com a ferramenta de corda.',
        idsObjetos: [],
      }
    }
  }
}

/** Avalia o checklist inteiro para a cena: os itens da mesa pela montagem, os de campo pelas marcações salvas. */
export function avaliarChecklistAltura(cena: CenaTabletop): ChecklistAvaliado {
  const avisos = avisosDaCena(cena)
  const marcacoes = cena.checklistAltura ?? {}
  const avaliados = ITENS_CHECKLIST_ALTURA.map((item): ItemAvaliado => {
    if (item.daMesa) return { item, ...avaliarDaMesa(cena, item.daMesa, avisos) }
    return { item, situacao: marcacoes[item.id] ?? 'pendente', idsObjetos: [] }
  })
  return {
    secoes: SECOES_CHECKLIST_ALTURA.map((s) => ({ id: s.id, titulo: s.titulo, itens: avaliados.filter((a) => a.item.secao === s.id) })),
    emAberto: avaliados.filter((a) => a.situacao === 'pendente' || a.situacao === 'nao-atende').length,
    total: avaliados.length,
    temTrabalhador: cena.objetos.some((o) => o.tipo === 'trabalhador' && !o.oculto),
  }
}

const ITENS_DE_CAMPO = new Set(ITENS_CHECKLIST_ALTURA.filter((i) => !i.daMesa).map((i) => i.id))

/**
 * Marca (ou desmarca, com `null`) um item de campo. Itens da mesa e ids
 * desconhecidos não mudam a cena. Sem nenhuma marcação, o campo sai da cena.
 */
export function marcarItemChecklistAltura(cena: CenaTabletop, idItem: string, marcacao: MarcacaoChecklistAltura | null): CenaTabletop {
  if (!ITENS_DE_CAMPO.has(idItem)) return cena
  const atual = cena.checklistAltura ?? {}
  if ((atual[idItem] ?? null) === marcacao) return cena
  const { [idItem]: _removido, ...resto } = atual
  const proximo: Record<string, MarcacaoChecklistAltura> = marcacao === null ? resto : { ...resto, [idItem]: marcacao }
  const { checklistAltura: _antigo, ...semChecklist } = cena
  return Object.keys(proximo).length === 0 ? semChecklist : { ...semChecklist, checklistAltura: proximo }
}
